import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  BarChart3, 
  Wallet, 
  RefreshCw, 
  Users,
  BookOpen,
  TrendingUp,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ArrowRightLeft,
  Building2,
  Activity
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/book-of-record', icon: Users, label: 'Book of Record', subtitle: 'Fundbase' },
  { path: '/ledger', icon: Building2, label: 'Asset Ledger' },
  { path: '/nav-pricing', icon: TrendingUp, label: 'NAV & Pricing' },
  { path: '/order-crossing', icon: ArrowRightLeft, label: 'Order Crossing' },
  { path: '/predictions', icon: BarChart3, label: 'Predictions' },
  { path: '/rebalancing', icon: RefreshCw, label: 'Rebalancing' },
];

const Sidebar = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const allNavItems = user?.role === 'admin'
    ? [...navItems, { path: '/activity', icon: Activity, label: 'Activity Log' }]
    : navItems;

  return (
    <aside className={cn(
      "fixed left-0 top-0 z-40 h-screen bg-[#1a1a3e] text-white transition-all duration-300",
      collapsed ? "w-16" : "w-64"
    )}>
      {/* Logo */}
      <div className="flex h-16 items-center justify-between px-4 border-b border-white/10">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#FF8C42] flex items-center justify-center text-sm text-white" style={{ fontFamily: 'Georgia, serif' }}>
              t
            </div>
            <div className="leading-tight">
              <span className="text-lg" style={{ fontFamily: 'Georgia, serif', letterSpacing: '-0.5px' }}>
                trove <span className="font-light">helix</span><span className="text-[#FF8C42]">.</span>
              </span>
              <p className="text-[10px] text-white/40 tracking-[0.2em] uppercase font-sans">Liquidity Engine</p>
            </div>
          </div>
        )}
        {collapsed && (
          <div className="w-8 h-8 rounded-lg bg-[#FF8C42] flex items-center justify-center text-sm text-white mx-auto" style={{ fontFamily: 'Georgia, serif' }}>
            t
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCollapsed(!collapsed)}
          className="text-white/70 hover:text-white hover:bg-white/10 h-8 w-8"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="p-3 space-y-1">
        {!collapsed && (
          <p className="text-xs text-white/50 uppercase tracking-wider px-3 mb-3">
            Helix Engine
          </p>
        )}
        {allNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path || 
            (item.path !== '/' && location.pathname.startsWith(item.path));
          
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                isActive 
                  ? "bg-[#FF8C42] text-white" 
                  : "text-white/70 hover:bg-white/10 hover:text-white",
                collapsed && "justify-center"
              )}
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>

      {/* Bottom Section */}
      <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-white/10">
        <NavLink
          to="/settings"
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-colors",
            collapsed && "justify-center"
          )}
        >
          <Settings className="h-5 w-5" />
          {!collapsed && <span className="text-sm font-medium">Settings</span>}
        </NavLink>
        <button
          onClick={handleLogout}
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/70 hover:bg-red-500/20 hover:text-red-400 transition-colors w-full",
            collapsed && "justify-center"
          )}
        >
          <LogOut className="h-5 w-5" />
          {!collapsed && <span className="text-sm font-medium">Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
